import MoveableHelper from "moveable-helper";
import { Frame } from "scenejs";
import { fitPoints, getAreaSize, getOverlapSize, isInside } from "overlap-area";

import _ from "lodash";
import { WidgetRegistry } from "../widgets/registry";
import { PAGE, ASSET, CAROUSEL, CAROUSEL_CARD, HOTSPOT, SECTION_COLUMN, CONTAINER, SLIDE, LIVE_ELEMENT, LIVE_ELEMENT_CONTAINER, MENU } from "constants/widgetTypes";

export default class MoveableData extends MoveableHelper {
  constructor(editorSession, eventBus, breakpoint) {
    super({
      createAuto: true,
      useBeforeRender: true,
    });

    this.editorSession = editorSession;
    this.eventBus = eventBus;
    this.breakpoint = breakpoint;
    this.containers = [];
    this.selectedTargets = []

    this.reparentTarget = null;

    // rAF throttle state for hitTest
    this._hitTestRafId = null;
    this._lastHitTestEvent = null;
  }

  setSelectedTargets(targets) {
    this.selectedTargets = targets;
  }

  getSelectedTargets() {
    return this.selectedTargets;
  }

  addContainer(container) {
    this.removeContainer(container);
    this.containers.push(container);
  }

  removeContainer(container) {
    if (!container) {
      return;
    }

    this.containers = this.containers.filter(item => {
      return item.id != container.id;
    });
  }

  onWidgetDrag = (e) => {
    this.onDrag(e); // Direct CSS transform — always runs at full rate

    // Throttle container detection to display refresh rate (~60fps)
    this._lastHitTestEvent = e;
    if (this._hitTestRafId) return; // Already scheduled for next frame
    this._hitTestRafId = requestAnimationFrame(() => {
      this._hitTestRafId = null;
      if (this._lastHitTestEvent) {
        this.hitTest(this._lastHitTestEvent);
      }
    });
  }

  onWidgetDragEnd = (e) => {
    if (this._hitTestRafId) {
      cancelAnimationFrame(this._hitTestRafId);
      this._hitTestRafId = null;
      // Run hitTest synchronously now — ensures reparentTarget is current for drag end
      if (this._lastHitTestEvent) {
        this.hitTest(this._lastHitTestEvent);
      }
    }
    this._lastHitTestEvent = null;

    const moveableManager = e.currentTarget;
    const { target } = moveableManager.state;

    const widget = target.dataset.widgetId;

    if (this.reparentTarget) {
      this.eventBus.trigger('onParentChange', {
        widget: widget,
        parent: this.reparentTarget
      });
    }

    const widgetType = target.dataset.widgetType;
  }

  skipContainer(container, widgetType, widget, containers, processedContainers, isFreeSize) {
    const containerWidgetType = container.getAttribute('data-widget-type');
    
    const skipConditions = [
      {
        widgetType: LIVE_ELEMENT,
        containerType: LIVE_ELEMENT_CONTAINER
      },
      {
        widgetType: CAROUSEL,
        containerType: CAROUSEL_CARD
      }
    ];
  
    const skipCondition = skipConditions.find(condition => 
      widgetType === condition.widgetType && containerWidgetType === condition.containerType
    );
  
    if (skipCondition) {
      const widgetElement = document.querySelector(`[data-widget-id="${widget}"]`);
      if (widgetElement && widgetElement.contains(container)) {
        const nextContainer = this.findNextValidContainer(containers, container, widgetType, isFreeSize);
        if (nextContainer && !processedContainers.includes(nextContainer)) {
          processedContainers.push(nextContainer);
        }
        return true;
      }
    }
    
    return false;
  }

  hitTest = (e) => {
    if (_.size(this.containers) == 0) {
      return;
    }

    if(e.clientX === undefined && e.clientY === undefined){
      return;
    }

    const moveableManager = e.currentTarget;
    const { target, pos1, pos2, pos3, pos4, targetClientRect } = moveableManager.state;
    const points = fitPoints([pos1, pos2, pos4, pos3], targetClientRect);

    const widget = target.dataset.widgetId;
    const widgetType = target.dataset.widgetType;
    const widgetConfig = WidgetRegistry.moveableConfig(this.editorSession, widgetType); 

    const currentParent = getCurrentParent(target);
    const widgetRect = target.getBoundingClientRect();

    //get html dom elements at the current position 
    const elements = document.elementsFromPoint(e.clientX, e.clientY);
    let containers = Array.from(elements).map(el => {
      return el.closest(`[data-container="true"]`);
    })

    const pageContainer = Array.from(elements).find(el => {
      return /^page-.+/.test(el.id);
    })

    if (pageContainer) {
      const idWithoutPrefix = pageContainer.id.substring(5);
      const pageElement = document.getElementById(idWithoutPrefix);

      containers.unshift(pageElement);
    }

    containers = containers.filter(el => {
      if(!el) {
        return false;
      }
      
      var canReparent = true;
      if(widgetConfig && widgetConfig.onReparent){
        canReparent = widgetConfig.onReparent(widget, el.id);
      }

      if (!canReparent) {
        return false;
      }

      const clientRect = el.getBoundingClientRect();
      let { left, top, width, height } = clientRect;
      if (el.dataset.widgetType === PAGE && this.breakpoint === 'desktop') {
        left = 22;
        width = window.innerWidth - 22;
      }
      const size = getOverlapSize(points, [
        [left, top],
        [left + width, top],
        [left + width, top + height],
        [left, top + height],
      ]);

      const totalSize = getAreaSize(points);

      if (!size || !totalSize) {
        return false;
      }

      const rate = Math.min(100, size / totalSize * 100);
      if (rate === 100) return true;

      if (e.clientX !== undefined && e.clientY !== undefined) {

        const isInside =
        e.clientX >= left &&
        e.clientX <= left + width &&
        e.clientY >= top &&
        e.clientY <= top + height;

        return isInside;
      }

      return false;
    });
    //check the list of containers and remove parent containers if child exists
    const processedContainers = [];
    let isFreeSize;
    

    if (widgetType === CAROUSEL) {
      const firstChild = target.firstChild;
      const childWidgetType = firstChild.getAttribute('data-widget-type');

      if (childWidgetType === CAROUSEL_CARD){
        isFreeSize = firstChild.getAttribute('data-free-size');
      }
    }

    containers.forEach(container => {

      if (this.skipContainer(container, widgetType, widget, containers, processedContainers, isFreeSize)) {
        return;
      }

      var found = containers.find(item => {
        if (container.getAttribute('data-widget-type') === HOTSPOT && item.getAttribute('data-widget-type') === CAROUSEL_CARD && isFreeSize){
          return false; 
        }
        return item != container && container.contains(item);
      });

      if (!found && !processedContainers.includes(container) && !container.classList.contains("cs_live_element_container")) {
        processedContainers.push(container);
      }
    });

    const directParentContainer = target.closest('[data-container="true"]')?.parentElement?.closest('[data-container="true"]');

    if (DIRECT_CONTAINER_TYPES.includes(widgetType) && directParentContainer) {
      const filteredContainers = processedContainers.filter(container => 
        container.id !== directParentContainer.id
      );
      processedContainers.length = 0;
      processedContainers.push(...filteredContainers);
    }

    var request = {
      widget: widget
    }

    var targetParent = null;
    var targetParentType = null;
    if (_.size(processedContainers) > 0) {
      targetParent = processedContainers[0];
      if (targetParent.dataset.widgetType !== HOTSPOT && widgetType === CAROUSEL && isFreeSize){
        for (const processContainer of processedContainers){
          if (processContainer.getAttribute('data-widget-type') === HOTSPOT ){
            targetParent = processContainer;
          }
        }
      }
    }

    if(targetParent){
      targetParentType = targetParent.dataset.widgetType;
      
      let clientRect = targetParent.getBoundingClientRect();
      let { left, top, width, height } = clientRect;

      if (targetParentType === PAGE && this.breakpoint === 'desktop') {
        left = 22;
        width = window.innerWidth - 22;

        // important: recalc right, otherwise overlap logic is wrong
        clientRect = {
          top,
          left,
          width,
          height,
          right: window.innerWidth,
          bottom: top + height
        };
      }

      const allowWidgetTypes = [PAGE, SLIDE, SECTION_COLUMN, CONTAINER, widgetType !== CAROUSEL && CAROUSEL_CARD, widgetType !== CAROUSEL && HOTSPOT];

      let isAssetReparent;

      isAssetReparent = allowReparentCondition(allowWidgetTypes, widgetType, isAssetReparent, targetParent, isFreeSize);

      if (currentParent?.id == targetParent.id) {

        const gridOutLeft = targetParent.querySelector('#gridOutLeft');
        const gridOutRight = targetParent.querySelector('#gridOutRight');

        const isIntersecting = (rect1, rect2) => {
            return !(rect2.left > rect1.right || 
                    rect2.right < rect1.left || 
                    rect2.top > rect1.bottom ||
                    rect2.bottom < rect1.top);
        };

        const gridOutLeftRect = gridOutLeft ? gridOutLeft.getBoundingClientRect() : null;
        const gridOutRightRect = gridOutRight ? gridOutRight.getBoundingClientRect() : null;

        const isHoveredOverGridOutLeft = gridOutLeftRect && isIntersecting(widgetRect, gridOutLeftRect);
        const isHoveredOverGridOutRight = gridOutRightRect && isIntersecting(widgetRect, gridOutRightRect);

        if (isHoveredOverGridOutLeft || isHoveredOverGridOutRight) {
          request = {
            ...request,
            parent: targetParent,
            rect: {
              top, left, width, height
            },
            gridOut: true
          }
          this.eventBus.trigger('onParentHover', request);
        } else {
          this.eventBus.trigger('onParentChange', {
            widget: widget,
            parent: this.reparentTarget
          })
        }
      }
         
      if (this.reparentTarget && this.reparentTarget.id === targetParent.id) {
        //No need to retrigger 
        return;
      }

      if (currentParent?.id != targetParent.id && isAssetReparent) {
        request = {
          ...request,
          parent: targetParent,
          rect: {
            top, left, width, height
          }
        }
      }
    }

    this.reparentTarget = request.parent;
    this.eventBus.trigger('onParentHover', request);
  }

  findNextValidContainer(containers, excludeContainer, widgetType, isFreeSize) {
    const remainingContainers = containers.filter(container => 
      container !== excludeContainer && 
      !excludeContainer.contains(container)
    );
    
    const sortedContainers = remainingContainers.sort((a, b) => {
      const aDepth = this.getElementDepth(a);
      const bDepth = this.getElementDepth(b);
      return bDepth - aDepth;
    });

    for (const container of sortedContainers) {
      const allowWidgetTypes = [PAGE, SLIDE, SECTION_COLUMN, CONTAINER, widgetType !== CAROUSEL && CAROUSEL_CARD, widgetType !== CAROUSEL && HOTSPOT];
      
      let isValidReparent = allowReparentCondition(allowWidgetTypes, widgetType, false, container, isFreeSize);
      if (isValidReparent) {
        return container;
      }
    }

    return null;
  }

  getElementDepth(element) {
    let depth = 0;
    let current = element;
    while (current.parentElement) {
      depth++;
      current = current.parentElement;
    }
    return depth;
  }

  onResize = (e) => {
    const { direction } = e;
    if (direction[0] != 0 && direction[1] != 0) {

      const target = e.target;
      const rect = target.getBoundingClientRect();
      const frame = this.getFrame(target);

      const width = parseFloat(rect.width);
      const height = parseFloat(rect.height);
      const ratio = width / height;

      frame.set("width", `${e.width}px`);
      frame.set("height", `${e.width / ratio}px`);

      this.testDrag(e.drag);
    } else {
      this.testResize(e);
    }

    this.testRender(e.target);
  }

  testDrag(e) {
    const target = e.target;
    const translate = e.translate;

    const frame = this.getFrame(target);
    let tx = translate[0];
    let ty = translate[1];

    const editorElement = document.querySelector('.editor_widget_viewer');
    const editorWidth = parseInt(getComputedStyle(editorElement).getPropertyValue('--editor-width'), 10);
    const updatedX = (editorWidth - 1200)/2;
    tx = Math.min(tx, editorWidth - updatedX);

    const newX = `${tx}px`;
    const newY = `${ty}px`;

    if (frame.has("transform", "translate")) {
      frame.set("transform", "translate", `${newX},${newY}`);
    } else {
      frame.set("transform", "translateX", newX);
      frame.set("transform", "translateY", newY);
    }

    const widgetId = e.target.dataset.widgetId;
    const type = e.target.dataset.widgetType;
    const widgetConfig = WidgetRegistry.moveableConfig(this.editorSession, type);

    if (widgetConfig && widgetConfig.onDrag) {
      var layout = frame.get();
      layout = widgetConfig.onDrag(layout, widgetId);
      const newFrame = new Frame(layout);
      this.setFrame(target, newFrame);
    }
  }
}

const DIRECT_CONTAINER_TYPES = [MENU, CONTAINER];

function getCurrentParent(target) {
  // Step 1: get the closest container
  let currentParent = target.closest('[data-container="true"]');

  // Step 2: check if target is a menu widget or container
  if (DIRECT_CONTAINER_TYPES.includes(target?.dataset?.widgetType)) {
    // Step 3: find the next closest container that is a "page"
    let nextParent = currentParent?.parentElement?.closest('[data-container="true"]');
    while (nextParent) {
      // Skip SECTION_COLUMN - look for parent of parent
      if (nextParent?.dataset?.widgetType === SECTION_COLUMN && target?.dataset?.widgetType === CONTAINER) {
        nextParent = nextParent.parentElement?.closest('[data-container="true"]');
        continue;
      }
      
      if (nextParent?.dataset?.widgetType === PAGE) {
        currentParent = nextParent;
        break;
      }
      nextParent = nextParent.parentElement?.closest('[data-container="true"]');
    }
  }

  return currentParent;
}


function allowReparentCondition(allowWidgetTypes, widgetType, isAssetReparent, targetParent, isFreeSize = false) {
  function isWidgetTypeAllowed(widgetType) {
    return allowWidgetTypes.includes(widgetType);
  }

  if (
    widgetType === ASSET ||
    (widgetType === CAROUSEL && (
      !isFreeSize ||
      targetParent?.getAttribute('data-widget-type') === CAROUSEL_CARD ||
      (isFreeSize && targetParent?.getAttribute('data-widget-type') !== HOTSPOT)
    ))
  ){
    isAssetReparent = (
      isWidgetTypeAllowed(targetParent.getAttribute('data-widget-type'))
    );
  } else {
    isAssetReparent = true;
  }
  return isAssetReparent;
}

